import { Col, Row } from "antd";
import React, { useRef } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { NextIcon } from "../../../../../../assets/svg";
import { StepCard, type StepCardAction } from "../../../../../molecules/StepCard";
import {
  AddNewTor,
  DEFAULT_TOR_RULES_FORM,
  PreviewTor,
  usePreviousTermTorRulesSnapshot,
  useSaveTorRules,
  type TorRulesFormValues,
} from "../../../../BookingForm/components/Commercial/tor-rules";
import { useBookingSelector } from "../../../../BookingForm/hooks/useBookingSelector";
import type { CommercialRenewalStepProps } from "../types";

export const TorRulesStep = (props: CommercialRenewalStepProps) => {
  const newTermForm = useForm<TorRulesFormValues>({
    defaultValues: DEFAULT_TOR_RULES_FORM,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  return (
    <FormProvider {...newTermForm}>
      <TorRulesStepContent {...props} />
    </FormProvider>
  );
};

const TorRulesStepContent = (props: CommercialRenewalStepProps) => {
  const { title, onDiscard, onNext, onBack, isSentForApproval } = props;
  const bookingDetails = useBookingSelector();
  const { leaseId = bookingDetails.leaseId } = useParams();
  const { handleSubmit } = useFormContext<TorRulesFormValues>();
  const copyIntoNewTermRef = useRef<((values: TorRulesFormValues) => void) | null>(null);
  const { saveTorRules, isSaving } = useSaveTorRules();

  const { previousTermValues, hasPreviousRules } = usePreviousTermTorRulesSnapshot({
    leaseId,
    source: {
      parentTorRules: bookingDetails?.parentTorRules,
      parentTorRuleCondition: bookingDetails?.parentTorRuleCondition,
      torRules: bookingDetails?.torRules,
      torRuleCondition: bookingDetails?.torRuleCondition,
    },
  });

  const renewFor = bookingDetails?.bookingTerms?.numberOfRenewalTerms ?? 1;
  const durationType = bookingDetails?.bookingTerms?.durationType ?? "years";
  const durationLabel = durationType === "months" ? "month(s)" : "year(s)";

  const handleCopyFromPrevious = () => {
    copyIntoNewTermRef.current?.(previousTermValues);
  };

  const saveValidatedTorRules = async (values: TorRulesFormValues) => {
    return saveTorRules(leaseId, values);
  };

  const onSaveDraft = handleSubmit(async (values) => {
    await saveValidatedTorRules(values);
  });

  const onSubmitNext = isSentForApproval
    ? () => onNext?.()
    : handleSubmit(async (values) => {
        const saved = await saveValidatedTorRules(values);
        if (!saved) return;
        onNext?.();
      });

  const actions: StepCardAction[] = [
    { key: "discard", label: "Discard", variant: "secondary", onClick: onDiscard },
    ...(isSentForApproval
      ? []
      : [
          {
            key: "draft",
            label: isSaving ? "Saving..." : "Save Draft",
            variant: "subtle" as const,
            onClick: onSaveDraft,
            disabled: isSaving,
          },
        ]),
    {
      key: "next",
      label: "Next",
      variant: "primary",
      icon: <NextIcon width="16px" height="16px" />,
      iconPosition: "end",
      onClick: onSubmitNext,
      disabled: isSaving,
    },
  ];

  return (
    <StepCard title={title} onBack={onBack} actions={actions}>
      <Row gutter={24} align="top">
        <Col span={12}>
          <PreviewTor
            values={previousTermValues}
            hasPreviousRules={hasPreviousRules}
            onCopy={handleCopyFromPrevious}
          />
        </Col>
        <Col span={12}>
          <AddNewTor
            disabled={isSaving}
            onRegisterCopy={(copyFn) => {
              copyIntoNewTermRef.current = copyFn;
            }}
            footerNote={`Applies to all ${renewFor} ${durationLabel} of the renewal. Turnover reports are captured against the schedule from Step 2.`}
          />
        </Col>
      </Row>
    </StepCard>
  );
};
